import React, { useState } from 'react';
import { _T, C } from '../../shared/theme';
import { s_PB, s_GB, s_GC, s_PL } from '../../shared/styles';
import { loadGameHistory, weekBoundaries, avgScoreInWindow, lastNScores } from '../../shared/utils';
import Sparkline from '../../components/ui/Sparkline';

function GameScoresTab({lang}) {
  const nl = lang==="nl";
  const GAMES = [
    {id:"memory",          icon:"🧠", label:"Memory",           labelNL:"Geheugen",         color:"#7B6FD0", unit:nl?"zetten":"moves",  invert:true,  base:100},
    {id:"reaction",        icon:"⚡", label:"Reaction",         labelNL:"Reactietijd",       color:"#D4873A", unit:"ms",                  invert:true,  base:1000},
    {id:"math",            icon:"➕", label:"Math",             labelNL:"Rekenen",           color:"#5B9ED0", unit:"pts",                 invert:false, base:0},
    {id:"sequence",        icon:"🔢", label:"Sequence",         labelNL:"Reeks",             color:"#2A9E82", unit:"level",               invert:false, base:0},
    {id:"stroop",          icon:"🌈", label:"Stroop",           labelNL:"Stroop",            color:"#7B6FD0", unit:"pts",                 invert:false, base:0},
    {id:"breathgame",      icon:"🌬️", label:"Breath Pacer",     labelNL:"Ademspel",          color:"#2A9E82", unit:"pts",                 invert:false, base:0},
    {id:"focustap",        icon:"🎯", label:"Focus Tap",        labelNL:"Focus Tap",         color:"#4AC87A", unit:"pts",                 invert:false, base:0},
    {id:"oddsymbol",       icon:"🔍", label:"Odd Symbol",       labelNL:"Vreemd Symbool",    color:"#D4873A", unit:"pts",                 invert:false, base:0},
    {id:"directionflash",  icon:"🧭", label:"Direction Flash",  labelNL:"Richting Flash",    color:"#C06090", unit:"ms",                  invert:true,  base:1500},
    {id:"peripheralflash", icon:"👁", label:"Peripheral Flash", labelNL:"Perifeer",          color:"#C8A84B", unit:"ms",                  invert:true,  base:1500},
    {id:"numchain",        icon:"🔗", label:"Number Chain",     labelNL:"Nummerketen",       color:"#5B9ED0", unit:"pts",                 invert:false, base:0},
    {id:"wordmemory",      icon:"📝", label:"Word Memory",      labelNL:"Woordgeheugen",     color:"#7B6FD0", unit:"pts",                 invert:false, base:0},
    {id:"tonedecoder",     icon:"👂", label:"Tone Decoder",     labelNL:"Toonherkenning",    color:"#5B9ED0", unit:"pts",                 invert:false, base:0},
    {id:"empathymirror",   icon:"🪞", label:"Empathy Mirror",   labelNL:"Empathiespiegel",   color:"#7B6FD0", unit:"pts",                 invert:false, base:0},
    {id:"patternmatch",    icon:"🔮", label:"Pattern Match",    labelNL:"Patroon Match",     color:"#7B6FD0", unit:"pts",                 invert:false, base:0},
    {id:"colorflood",      icon:"🌊", label:"Color Flood",      labelNL:"Kleurvloed",        color:"#5B9ED0", unit:"pts",                 invert:false, base:0},
    {id:"mentalmaze",      icon:"🧩", label:"Mental Maze",      labelNL:"Mentaal Doolhof",   color:"#4AC87A", unit:"pts",                 invert:false, base:0},
    {id:"dualback",        icon:"🧠", label:"Dual N-Back",      labelNL:"Dual N-Terug",      color:"#C06090", unit:"pts",                 invert:false, base:0},
    {id:"speedsort",       icon:"⚡", label:"Speed Sort",       labelNL:"Snel Sorteren",     color:"#D4873A", unit:"pts",                 invert:false, base:0},
    {id:"soundreact",      icon:"🔔", label:"Sound Reaction",   labelNL:"Geluid Reactie",    color:"#D4873A", unit:"ms",                  invert:true,  base:2000},
    {id:"soundmemory",     icon:"🎵", label:"Sound Memory",     labelNL:"Geluidsgeheugen",   color:"#7B6FD0", unit:"pts",                 invert:false, base:0},
    {id:"pitchmatch",      icon:"🎶", label:"Pitch Match",      labelNL:"Toonhoogte",        color:"#4AC87A", unit:"pts",                 invert:false, base:0},
    {id:"rhythmcopy",      icon:"🥁", label:"Rhythm Copy",      labelNL:"Ritme Kopiëren",    color:"#C8A84B", unit:"pts",                 invert:false, base:0},
    {id:"soundlocate",     icon:"👂", label:"Sound Locate",     labelNL:"Geluid Lokaliseren",color:"#C06090", unit:"pts",                 invert:false, base:0},
    {id:"timinggame",      icon:"⏱", label:"Time Judge",       labelNL:"Tijdrechter",       color:"#4AC87A", unit:"ms",                  invert:true,  base:5000},
    {id:"flashequation",   icon:"🌀", label:"Flash Equation",   labelNL:"Flits Formule",     color:"#D4873A", unit:"pts",                 invert:false, base:0},
    {id:"gridmemory",      icon:"🔲", label:"Grid Memory",      labelNL:"Raster Geheugen",   color:"#C06090", unit:"pts",                 invert:false, base:0},
  ];
  const [selGame, setSelGame] = useState("memory");
  const game = GAMES.find(g=>g.id===selGame);

  const hist = loadGameHistory(selGame);
  const sorted = [...hist].sort((a,b)=>b.ts-a.ts);

  const toDisplay = (raw) => {
    if (!game) return raw;
    if (game.invert) return game.base - raw;
    return raw;
  };
  const formatVal = (raw) => {
    const v = toDisplay(raw);
    if (game.unit==="ms") return `${v}ms`;
    if (game.id==="sequence") return `Lv ${v}`;
    return `${v} ${game.unit}`;
  };
  const formatDate = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString(nl?"nl-NL":"en-US",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"});
  };
  const bestRaw = hist.length ? (game.invert ? Math.max(...hist.map(h=>h.score)) : Math.max(...hist.map(h=>h.score))) : null;
  const bestDisplay = bestRaw !== null ? toDisplay(bestRaw) : null;

  // Mini bar chart: last 10 sessions
  const last10 = [...hist].sort((a,b)=>a.ts-b.ts).slice(-10);
  const vals = last10.map(h=>toDisplay(h.score));
  const minV = vals.length ? Math.min(...vals) : 0;
  const maxV = vals.length ? Math.max(...vals) : 1;
  const range = maxV - minV || 1;

  return (
    <div className="fadeUp">
      {/* Game selector */}
      <div style={{display:"flex",gap:6,flexWrap:"nowrap",overflowX:"auto",marginBottom:14,paddingBottom:4}}>
        {GAMES.map(g=>(
          <button key={g.id} onClick={()=>setSelGame(g.id)}
            style={{...s_PL(selGame===g.id, g.color), padding:"7px 12px", flexShrink:0}}>
            {g.icon} {nl?g.labelNL:g.label}
          </button>
        ))}
      </div>

      {hist.length===0 ? (
        <div style={{...s_GC({textAlign:"center",padding:32}),color:C.silver,fontSize:13}}>
          {nl?"Nog geen sessies gespeeld voor deze game.":"No sessions played for this game yet."}<br/>
          <span style={{fontSize:11,marginTop:6,display:"block"}}>🎮 {nl?"Speel de game om je scores bij te houden!":"Play the game to track your scores!"}</span>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:14}}>
            {[
              {label:nl?"Sessies":"Sessions", val:hist.length, color:_T.accent},
              {label:nl?"Beste":"Best",        val:bestDisplay!==null?formatVal(bestRaw):"–", color:C.teal},
              {label:nl?"Gemiddeld":"Avg",     val: hist.length ? formatVal(Math.round(hist.reduce((a,h)=>a+h.score,0)/hist.length)) : "–", color:C.purple},
            ].map(c=>(
              <div key={c.label} style={s_GC({padding:"10px 12px",marginBottom:0,textAlign:"center"})}>
                <div style={{fontSize:16,color:c.color,fontWeight:700,marginBottom:2}}>{c.val}</div>
                <div style={{fontSize:9,color:C.silver}}>{c.label.toUpperCase()}</div>
              </div>
            ))}
          </div>

          {/* Mini bar chart */}
          {last10.length >= 2 && (
            <div style={s_GC({padding:"14px 16px",marginBottom:14})}>
              <div style={{fontSize:13,color:_T.accent,letterSpacing:2,fontWeight:600,marginBottom:10}}>{nl?"LAATSTE 10 SESSIES":"LAST 10 SESSIONS"}</div>
              <div style={{display:"flex",alignItems:"flex-end",gap:4,height:48}}>
                {last10.map((h,i)=>{
                  const v = toDisplay(h.score);
                  const pct = (v - minV) / range;
                  const isLast = i===last10.length-1;
                  return (
                    <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                      <div style={{width:"100%",background:isLast?game.color:`${game.color}55`,borderRadius:"3px 3px 0 0",height:`${Math.max(8,pct*40+8)}px`,transition:"height 0.3s"}}/>
                    </div>
                  );
                })}
              </div>
              <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
                <span style={{fontSize:9,color:C.silver}}>{nl?"Oudst":"Oldest"}</span>
                <span style={{fontSize:9,color:C.silver}}>{nl?"Nieuwst":"Latest"}</span>
              </div>
            </div>
          )}

          {/* Full history list */}
          <div style={{fontSize:13,color:_T.accent,letterSpacing:2,fontWeight:600,marginBottom:8}}>{nl?"ALLE SESSIES":"ALL SESSIONS"}</div>
          <div style={s_GC({padding:"6px 0"})}>
            {sorted.map((h,i)=>{
              const isBest = h.score === bestRaw;
              return (
                <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",borderBottom:i<sorted.length-1?`1px solid ${_T.cardBorderBot}`:"none"}}>
                  <div style={{width:28,height:28,borderRadius:"50%",background:isBest?`${game.color}25`:_T.bg4,border:`1.5px solid ${isBest?game.color:_T.cardBorderBot}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,color:isBest?game.color:C.silver,fontWeight:700,flexShrink:0}}>
                    {isBest?"🏆":`#${i+1}`}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{color:isBest?game.color:C.white,fontSize:13,fontWeight:isBest?700:500}}>{formatVal(h.score)}</div>
                    <div style={{fontSize:13,color:C.silver,marginTop:1}}>{formatDate(h.ts)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default GameScoresTab;
